'use client'

import { motion, useAnimation } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '../../utils/cn'
import { Button } from '../atoms'
import { Menu, X } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { useChat } from '../../lib/contexts/ChatContext'

export interface NavigationProps {
  className?: string
}

const navItems = [
  { href: '/purpose', label: 'Purpose' },
  { href: '/approach', label: 'Approach' },
  { href: '/workshops', label: 'Workshops' },
]

export function Navigation({ className }: NavigationProps) {
  const pathname = usePathname()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [isMasterclassUser, setIsMasterclassUser] = useState(false)
  const { userData } = useChat()

  // Check if the authenticated user is a masterclass participant
  // Also: @glueiq.com users are ALWAYS masterclass-only — never show Command Center
  useEffect(() => {
    if (!userData?.authenticated) return
    const email = (userData.email || '').toLowerCase()
    if (email.endsWith('@glueiq.com')) {
      setIsMasterclassUser(true)
      return
    }
    // probe form: a 200 either way, so a non-participant does not log a 404
    // on every page load just because this ran
    fetch('/api/masterclass/me?probe=1', { credentials: 'include' })
      .then(res => (res.ok ? res.json() : null))
      .then(json => {
        if (json?.data?.isParticipant) setIsMasterclassUser(true)
      })
      .catch(() => {})
  }, [userData?.authenticated, userData?.email])

  // Smart Navigation Show/Hide State
  const [isVisible, setIsVisible] = useState(true)
  const [isAtTop, setIsAtTop] = useState(true)
  const lastScrollY = useRef(0)
  const scrollThreshold = 50 // Minimum scroll movement to trigger
  const rafId = useRef<number>()

  const controls = useAnimation()

  /**
   * Smart Navigation Show/Hide Logic
   * - Hide on scroll down
   * - Show on scroll up
   * - Always show when at top of page
   * - Minimum 50px movement before triggering
   */
  useEffect(() => {
    const handleScroll = () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current)
      }

      rafId.current = requestAnimationFrame(() => {
        const currentScrollY = window.scrollY

        // Check if at top of page
        const atTop = currentScrollY < 10
        setIsAtTop(atTop)

        // Always show when at top
        if (atTop) {
          setIsVisible(true)
          setScrolled(false)
          controls.start({
            y: 0,
            opacity: 1,
            transition: { duration: 0.3, ease: 'easeOut' }
          })
          return
        }

        // Only trigger if scrolled past threshold
        const scrollDelta = Math.abs(currentScrollY - lastScrollY.current)

        if (scrollDelta > scrollThreshold) {
          const scrollingDown = currentScrollY > lastScrollY.current

          if (scrollingDown) {
            // Scrolling down - hide navigation
            setIsVisible(false)
            controls.start({
              y: -100,
              opacity: 0,
              transition: { duration: 0.3, ease: 'easeIn' }
            })
          } else {
            // Scrolling up - show navigation
            setIsVisible(true)
            controls.start({
              y: 0,
              opacity: 1,
              transition: { duration: 0.3, ease: 'easeOut' }
            })
          }

          lastScrollY.current = currentScrollY
        }

        // Track if page has been scrolled (for backdrop blur effect)
        setScrolled(currentScrollY > 10)
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll() // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (rafId.current) {
        cancelAnimationFrame(rafId.current)
      }
    }
  }, [controls])

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [pathname])

  // Get the height of the navigation for proper spacing (120px like Purpose page)
  const navHeight = 'h-16 md:h-[120px]'

  return (
    <>
      <motion.header
        initial={{ opacity: 1, y: 0 }}
        animate={controls}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          scrolled && "backdrop-blur-xl bg-[var(--hg-bg-secondary)]/90 border-b border-[var(--hg-border-color)]",
          className
        )}
      >
        <nav className={cn("container max-w-7xl mx-auto px-6", navHeight, "flex items-center")}>
          <div className="flex items-center justify-between w-full relative">
            {/* Logo */}
            <Link href="/" className="flex-shrink-0">
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                className="relative"
              >
                <Image
                  src="/hmn_logo.png"
                  alt="hmn"
                  width={213}
                  height={45}
                  className="w-[100px] md:w-[120px] lg:w-[140px] cursor-pointer"
                  priority
                />
              </motion.div>
            </Link>

            {/* Desktop Navigation - Centered */}
            <motion.div
              className="hidden md:flex items-center gap-4 absolute left-1/2 -translate-x-1/2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {navItems.map((item) => (
                <Link key={item.href} href={item.href}>
                  <motion.div
                    className={cn(
                      "backdrop-blur-md rounded-full px-6 py-3 text-white text-sm font-semibold transition-all cursor-pointer",
                      pathname === item.href
                        ? "bg-white/20 border border-white"
                        : "bg-white/20 hover:bg-white/30"
                    )}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {item.label}
                  </motion.div>
                </Link>
              ))}
            </motion.div>

            {/* CTA Button or Welcome Message - Right side */}
            <motion.div
              className="hidden md:flex items-center gap-3 flex-shrink-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {userData?.authenticated ? (
                <>
                  <div className="text-sm text-white/80 font-diatype whitespace-nowrap">
                    Hi, <span className="text-cyan-400 font-medium">{(userData.name || userData.email || '').split(' ')[0]}</span>
                  </div>
                  {isMasterclassUser ? (
                    <Link href="/masterclass">
                      <div className="backdrop-blur-md rounded-full px-4 lg:px-6 py-2.5 lg:py-3 text-sm font-semibold transition-all bg-cyan-400 text-gray-900 hover:bg-cyan-300 whitespace-nowrap">
                        My Masterclass
                      </div>
                    </Link>
                  ) : (userData.email || '').toLowerCase().endsWith('@behmn.com') ? (
                    <>
                      <Link href="/command-center">
                        <div className="backdrop-blur-md rounded-full px-4 lg:px-6 py-2.5 lg:py-3 text-sm font-semibold transition-all bg-cyan-400 text-gray-900 hover:bg-cyan-300 whitespace-nowrap">
                          Command Center
                        </div>
                      </Link>
                      <Link href="/dashboard">
                        <div className="backdrop-blur-md bg-white/20 rounded-full px-4 lg:px-6 py-2.5 lg:py-3 text-white text-sm font-semibold hover:bg-white/30 transition-all whitespace-nowrap">
                          Dashboard
                        </div>
                      </Link>
                    </>
                  ) : (
                    <Link href="/dashboard">
                      <div className="backdrop-blur-md bg-white/20 rounded-full px-4 lg:px-6 py-2.5 lg:py-3 text-white text-sm font-semibold hover:bg-white/30 transition-all whitespace-nowrap">
                        Dashboard
                      </div>
                    </Link>
                  )}
                </>
              ) : (
                <>
                  <Link href="/login">
                    <div className="backdrop-blur-md bg-white/20 rounded-full px-6 py-3 text-white text-sm font-semibold hover:bg-white/30 transition-all">
                      Login
                    </div>
                  </Link>
                  <a href="https://calendly.com/alex-behmn/discovery-call" target="_blank" rel="noopener noreferrer">
                    <div className="backdrop-blur-md rounded-full px-6 py-3 text-sm font-semibold transition-all bg-[#61d8fe] text-black hover:shadow-[0_0_20px_rgba(97,216,254,0.4)]">
                      Schedule a Call
                    </div>
                  </a>
                </>
              )}
            </motion.div>

            {/* Mobile menu button */}
            <motion.button
              className="md:hidden p-2 rounded-lg bg-white/20 backdrop-blur-md border border-white/30"
              whileTap={{ scale: 0.95 }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-white" />
              ) : (
                <Menu className="w-5 h-5 text-white" />
              )}
            </motion.button>
          </div>
        </nav>

        {/* Mobile Navigation - Full Screen Overlay */}
        <motion.div
          initial={false}
          animate={{
            opacity: mobileMenuOpen ? 1 : 0,
            pointerEvents: mobileMenuOpen ? 'auto' : 'none'
          }}
          className="md:hidden fixed inset-0 top-14 hg-bg-primary backdrop-blur-xl z-40"
          style={{ display: mobileMenuOpen ? 'block' : 'none' }}
        >
          <div className="container mx-auto px-6 py-8">
            <div className="space-y-4">
              {navItems.map((item) => (
                <Link key={item.href} href={item.href}>
                  <motion.div
                    whileTap={{ scale: 0.95 }}
                    className={cn(
                      "backdrop-blur-md rounded-full px-6 py-4 text-[var(--hg-text-primary)] text-lg font-semibold transition-all text-center",
                      pathname === item.href
                        ? "bg-[var(--hg-bg-tertiary)] border border-[var(--hg-border-strong)]"
                        : "bg-[var(--hg-bg-secondary)] hover:bg-[var(--hg-bg-tertiary)] border border-[var(--hg-border-color)]"
                    )}
                  >
                    {item.label}
                  </motion.div>
                </Link>
              ))}

              {/* Auth Buttons */}
              <div className="pt-6 border-t border-[var(--hg-border-color)] space-y-3">
                {userData?.authenticated ? (
                  <>
                    <div className="text-sm text-[var(--hg-text-secondary)] font-diatype mb-4 text-center">
                      Welcome, <span className="text-[var(--hg-text-primary)] font-medium">{userData.name || userData.email}</span>
                    </div>
                    {isMasterclassUser ? (
                      <Link href="/masterclass">
                        <div className="backdrop-blur-md rounded-full px-6 py-4 text-lg font-semibold text-center bg-[var(--hg-cyan-bg)] text-[var(--hg-cyan-text)] hover:opacity-90">
                          My Masterclass
                        </div>
                      </Link>
                    ) : (userData.email || '').toLowerCase().endsWith('@behmn.com') ? (
                      <>
                        <Link href="/command-center">
                          <div className="backdrop-blur-md rounded-full px-6 py-4 text-lg font-semibold text-center bg-[var(--hg-cyan-bg)] text-[var(--hg-cyan-text)] hover:opacity-90">
                            Command Center
                          </div>
                        </Link>
                        <Link href="/dashboard">
                          <div className="backdrop-blur-md bg-[var(--hg-bg-secondary)] border border-[var(--hg-border-color)] rounded-full px-6 py-4 text-[var(--hg-text-primary)] text-lg font-semibold text-center hover:bg-[var(--hg-bg-tertiary)] transition-all">
                            Dashboard
                          </div>
                        </Link>
                      </>
                    ) : (
                      <Link href="/dashboard">
                        <div className="backdrop-blur-md bg-[var(--hg-bg-secondary)] border border-[var(--hg-border-color)] rounded-full px-6 py-4 text-[var(--hg-text-primary)] text-lg font-semibold text-center hover:bg-[var(--hg-bg-tertiary)] transition-all">
                          Dashboard
                        </div>
                      </Link>
                    )}
                  </>
                ) : (
                  <>
                    <Link href="/login">
                      <div className="backdrop-blur-md bg-[var(--hg-bg-secondary)] border border-[var(--hg-border-color)] rounded-full px-6 py-4 text-[var(--hg-text-primary)] text-lg font-semibold text-center">
                        Login
                      </div>
                    </Link>
                    <a href="https://calendly.com/alex-behmn/discovery-call" target="_blank" rel="noopener noreferrer">
                      <div className="backdrop-blur-md rounded-full px-6 py-4 text-lg font-semibold text-center bg-[#61d8fe] text-black hover:shadow-[0_0_20px_rgba(97,216,254,0.4)]">
                        Schedule a Call
                      </div>
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.header>
    </>
  )
}
